import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { translations, Language } from './translations';

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  resetLanguage: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_KEY = 'kiosk_language';
const DEFAULT_LANGUAGE: Language = 'ko';

function getInitialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (saved === 'ko' || saved === 'en' || saved === 'ja' || saved === 'zh')) {
      return saved as Language;
    }
  } catch {
    // localStorage 접근 불가 시 기본값 사용
  }
  return DEFAULT_LANGUAGE;
}

// 언어별 HTML lang 속성 값 (CSS :lang() 셀렉터에서 폰트 분기에 사용)
const HTML_LANG_MAP: Record<Language, string> = {
  ko: 'ko',
  en: 'en',
  ja: 'ja',
  zh: 'zh-Hans', // 중국어 간체 (Korean 매장 → 중국 본토 관광객 대응)
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // 언어가 바뀔 때마다 <html lang> 속성 동기화 → CSS :lang() 셀렉터가 동작
  useEffect(() => {
    document.documentElement.lang = HTML_LANG_MAP[language];
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage 접근 불가 시 무시
    }
  }, []);

  const resetLanguage = useCallback(() => {
    setLanguageState(DEFAULT_LANGUAGE);
    try {
      localStorage.setItem(STORAGE_KEY, DEFAULT_LANGUAGE);
    } catch {
      // localStorage 접근 불가 시 무시
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let text = translations[language][key];
      if (text === undefined) {
        // 키가 없으면 한국어 폴백, 그것도 없으면 키 반환
        text = translations[DEFAULT_LANGUAGE][key] ?? key;
      }
      if (params) {
        Object.entries(params).forEach(([paramKey, value]) => {
          text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
        });
      }
      return text;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, resetLanguage, t }),
    [language, setLanguage, resetLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
